import { City } from '../types/chatTypes'; // Ensure City type is imported

/**
 * Utilities for extracting context from user messages
 */

/**
 * Convert a location string to a City object
 * Moved from HotelService for shared use
 */
export async function locationStringToCity(locationString: string): Promise<City | null> {
    try {
      console.log(`[locationStringToCity] Converting "${locationString}" to City object`);
      
      // Filter out invalid or vague location queries
      if (locationString.length < 2 || locationString.split(' ').length > 5) {
        console.log(`[locationStringToCity] Invalid location string: "${locationString}"`);
        return null;
      }
      
      // Try to lookup this location using the searchLocations function from the *hotel* service
      // Consider making this dependency injectable or using a more generic location search if needed elsewhere
      const { searchLocations } = await import('../../../hotels/services/hotelSearchService');
      const locations = await searchLocations(locationString);
      
      if (locations && locations.length > 0) {
        console.log(`[locationStringToCity] Found ${locations.length} matching locations, using first match`);
        // Ensure the returned object matches the City interface
        const firstLoc = locations[0];
        return {
          name: firstLoc.name,
          country: firstLoc.country,
          latitude: firstLoc.latitude,
          longitude: firstLoc.longitude,
          iataCode: firstLoc.iataCode
        };
      }
      
      // Fallback: For common cities, provide hardcoded coordinates
      const commonCityCoords: Record<string, {lat: number, lng: number, country: string}> = {
        'new york': {lat: 40.7128, lng: -74.0060, country: 'USA'},
        'london': {lat: 51.5074, lng: -0.1278, country: 'UK'},
        'paris': {lat: 48.8566, lng: 2.3522, country: 'France'},
        'tokyo': {lat: 35.6762, lng: 139.6503, country: 'Japan'},
        'rome': {lat: 41.9028, lng: 12.4964, country: 'Italy'},
        'sydney': {lat: -33.8688, lng: 151.2093, country: 'Australia'}
        // Consider adding Maldives coordinates if needed frequently
      };
      
      const lowercaseLocation = locationString.toLowerCase();
      for (const [city, coords] of Object.entries(commonCityCoords)) {
        if (lowercaseLocation.includes(city)) {
          console.log(`[locationStringToCity] Using hardcoded coordinates for "${city}"`);
          return {
            name: city.charAt(0).toUpperCase() + city.slice(1),
            country: coords.country,
            latitude: coords.lat,
            longitude: coords.lng
            // iataCode is missing here, might be needed depending on usage
          };
        }
      }
      
      console.log(`[locationStringToCity] No locations found, unable to resolve city`);
      return null;
    } catch (error) {
      console.error(`[locationStringToCity] Error converting location:`, error);
      return null;
    }
  }

/**
 * Extract a location mentioned in the user's message
 */
export function getLocationContext(query: string): string | undefined {
  // First, try to match common location patterns
  // Modified patterns with more specific boundaries to avoid capturing extra content
  const locationPatterns = [
    /(?:in|near|for|at)\s+([A-Za-z\s-]+?)(?:\s+is|\s+are|\s+has|\s+will|\s+during|\?|$|\.|,|\s+and|\s+with|\s+from|\s+to)/i,
    /hotels?\s+(?:in|near|at)\s+([A-Za-z\s-]+?)(?:\s+is|\s+are|\s+has|\s+will|\s+during|\?|$|\.|,|\s+and|\s+with|\s+from|\s+to)/i,
    /(?:visit|visiting|travel\s+to|traveling\s+to)\s+([A-Za-z\s-]+?)(?:\s+is|\s+are|\s+has|\s+will|\s+during|\?|$|\.|,|\s+and|\s+with|\s+from|\s+to)/i
  ];
  
  for (const pattern of locationPatterns) {
    const match = query.match(pattern);
    if (match && match[1]) {
      const location = match[1].trim();
      // Filter out invalid locations that are likely not actual places
      const invalidLocations = ['the', 'a', 'my', 'your', 'our', 'their', 'these', 'those', 'this', 'that', 'all', 'some', 'any', 'no', 'each', 'every', 'few', 'many', 'dates', 'date'];
      if (location.length > 1 && !invalidLocations.includes(location.toLowerCase())) {
        // Clean up the location before returning it
        const cleanedLocation = cleanLocationString(location);
        console.log(`[getLocationContext] Extracted location: "${cleanedLocation}"`);
        return cleanedLocation;
      }
    }
  }
  
  // Handle specific cities with known name formats
  const maldivesPattern = /\b(?:the\s+)?(maldives)\b/i;
  const maldivesMatch = query.match(maldivesPattern);
  if (maldivesMatch && maldivesMatch[1]) {
    console.log(`[getLocationContext] Found specific location: "${maldivesMatch[1]}"`);
    return maldivesMatch[1];
  }
  
  // If no location found with patterns, try to check if query contains specific city names
  const commonCities = ['new york', 'los angeles', 'chicago', 'houston', 'phoenix', 'philadelphia', 'san antonio', 'san diego', 'dallas', 'san jose', 'london', 'paris', 'tokyo', 'dubai', 'singapore', 'barcelona', 'rome', 'amsterdam', 'berlin', 'vienna', 'bangkok', 'hong kong', 'madrid', 'sydney', 'miami', 'toronto', 'istanbul'];
  
  const lowerQuery = query.toLowerCase();
  for (const city of commonCities) {
    if (lowerQuery.includes(city)) {
      console.log(`[getLocationContext] Found common city name: "${city}"`);
      return city;
    }
  }
  
  return undefined;
}

/**
 * Clean up location strings by removing time references
 */
export function cleanLocationString(location: string): string {
  // List of months, seasons, and time periods to remove from location names
  const timeTerms = [
    'january', 'february', 'march', 'april', 'may', 'june', 
    'july', 'august', 'september', 'october', 'november', 'december',
    'winter', 'spring', 'summer', 'fall', 'autumn',
    'morning', 'afternoon', 'evening', 'night',
    'today', 'tomorrow', 'yesterday', 'next week', 'last week',
    'this week', 'weekend', 'weekday'
  ];
  
  // Create a regex to match any of the time terms with word boundaries
  const timePattern = new RegExp(`\\b(in\\s+)?(${timeTerms.join('|')})\\b`, 'gi');
  
  // Remove time references
  const cleaned = location.replace(timePattern, '').trim();
  
  // Places that commonly include "the" in their formal name
  const placesWithThe = [
    'maldives', 'netherlands', 'philippines', 'uk', 'usa', 
    'united states', 'united kingdom', 'bahamas', 'caribbean',
    'dominican republic', 'uae', 'united arab emirates'
  ];
  
  // Check if we should preserve "the" in this location name
  const lowerLocation = cleaned.toLowerCase();
  const shouldPreserveThe = placesWithThe.some(place => 
    lowerLocation === `the ${place}` || placesWithThe.includes(lowerLocation)
  );
  
  // Remove "the" from the beginning if present and not in our exception list
  const withoutThe = shouldPreserveThe ? cleaned : cleaned.replace(/^the\s+/i, '');
  
  // Trim any extra whitespace that might have been created
  const result = withoutThe.replace(/\s+/g, ' ').trim();
  
  // Log if we made changes
  if (result !== location) {
    console.log(`[cleanLocationString] Cleaned "${location}" to "${result}"`);
  }
  
  return result || location; // Return original if cleaning resulted in empty string
}

/**
 * Extract travel preferences from user message
 */
export function extractItineraryPreferences(context: string): string[] {
  const preferences: string[] = [];
  if (context.includes('family') || context.includes('kids') || context.includes('children')) {
    preferences.push('family-friendly');
  }
  if (context.includes('budget') || context.includes('cheap')) {
    preferences.push('budget-friendly');
  }
  if (context.includes('luxury') || context.includes('high-end')) {
    preferences.push('luxury');
  }
  if (context.includes('romantic') || context.includes('couple')) {
    preferences.push('romantic');
  }
  if (context.includes('adventure') || context.includes('hiking') || context.includes('outdoors')) {
    preferences.push('adventure');
  }
  const durationMatch = context.match(/(\d+)\s*day/i);
  if (durationMatch && durationMatch[1]) {
      preferences.push(`${durationMatch[1]} day duration`);
  }

  return preferences;
}

/**
 * Determine user intent from their message
 */
export function determineIntent(query: string, fullContextQuery: string, needsWebSearch: boolean): 'hotel' | 'activity' | 'itinerary' | 'websearch' | 'general' {
  const lowerQuery = query.toLowerCase();
  const lowerContext = fullContextQuery.toLowerCase(); // Use the full context

  // Check for hotel intent (prioritize current query)
  const hotelRegex = /hotel/i;
  if (hotelRegex.test(lowerQuery)) {
    return 'hotel';
  }
  // Check context if not in current query
  if (hotelRegex.test(lowerContext)) {
    // Add a check to ensure the user isn't explicitly saying 'no' or changing topic
    if (!/no thanks|never mind|forget it|stop|cancel|what about/.test(lowerQuery)) {
       return 'hotel';
    }
  }

  // Check for activity intent (prioritize current query)
  const activityRegex = /\b(activit(?:y|ies)|tour|things to do|excursion|see|do)\b/i; // Added 'see', 'do', 'activities'
  if (activityRegex.test(lowerQuery)) {
    return 'activity';
  }
  // Check context if not in current query
  if (activityRegex.test(lowerContext)) {
     // Add a check to ensure the user isn't explicitly saying 'no' or changing topic
     if (!/no thanks|never mind|forget it|stop|cancel|what about/.test(lowerQuery)) {
        console.log("[determineIntent] Activity intent inferred from context.");
        return 'activity';
     }
  }

  // Check for itinerary intent (prioritize current query)
  const itineraryRegex = /\b(itinerary|plan|schedule|trip for)\b/i;
  if (itineraryRegex.test(lowerQuery)) {
    return 'itinerary';
  }
  // Check context if not in current query (already handled below, kept for consistency)
  if (itineraryRegex.test(lowerContext)) {
    // Add a check to ensure the user isn't explicitly saying 'no' or changing topic
    if (!/no thanks|never mind|forget it|stop|cancel|what about/.test(lowerQuery)) {
      return 'itinerary';
    }
  }

  // Check if needs web search (high priority if detected)
  if (needsWebSearch) {
    return 'websearch';
  }

  // Check context for follow-up itinerary generation (Redundant check removed, handled above)
  // if (/\b(itinerary|plan|schedule)\b/i.test(fullContextQuery)) {
  //   return 'itinerary';
  // }

  // Default to general intent
  return 'general';
} 